import type {CSSProperties} from 'react';
import React, {useState, useRef, useMemo} from 'react';
import cx from 'classnames';
import {Text, Popover, WithTooltip} from '@albert-io/atomic';
import type {Props as TextProps} from '@albert-io/atomic/atoms/Text/Text.react';

import {pluralize} from 'lib/stringUtils';
import {normalizePercentages} from 'lib/normalizePercentages';
import {PROFICIENCY_LEVELS} from 'client/AdaptiveSubjects/constants';
import {
  PROFICIENCY_SEGMENTS_BY_METHOD,
  PROFICIENCY_SEGMENT_SLOTS,
  getSegmentCount
} from 'client/Reports/lib/constants/proficiencySegments';
import LevelBadge from 'assets/images/adaptive/LevelBadge';

import {SegmentedBar} from './SegmentedBar/SegmentedBar';

import {getSelectedIndex, getPercentageLabel, getIconForIndex} from './ProficiencyChart.utils';

import './proficiency-chart.scss';

interface ProficiencyChartBadgesProps {
  proficiencyLevel?: number | null;
  showNotStarted?: boolean; // if true, shows 'Not started' copy, vs all empty badges
  showLabel?: boolean; // controls label shown on hover
  badgeSize?: number; // size of the badges, default is 24px
  className?: string;
  iconGap?: number; // gap between icons, default is 1 (space-x1;8px)
  withTooltip?: boolean; // if true, shows proficiency level shows when hovering over the badges
}

export const ProficiencyChartBadges = ({
  proficiencyLevel,
  showNotStarted = true,
  showLabel = true,
  badgeSize = 20,
  className,
  iconGap = 1,
  withTooltip = false
}: ProficiencyChartBadgesProps) => {
  const label = PROFICIENCY_LEVELS[proficiencyLevel ?? -1];

  if (!label && showNotStarted) {
    return (
      <div className='proficiency-chart__badges'>
        <Text size='s' color='tertiary'>
          Not started
        </Text>
      </div>
    );
  }

  const level = proficiencyLevel ?? 0;

  return (
    <div className={cx('proficiency-chart__badges', className, {})}>
      <WithTooltip
        enabled={withTooltip}
        theme='light'
        placement='right'
        content={`${PROFICIENCY_LEVELS[level]}`}
      >
        <div
          className='proficiency-chart__badges-icons'
          style={{
            gap: `var(--spacing-x${iconGap})`
          }}
        >
          {level >= 1 ? (
            <LevelBadge
              className='proficiency-chart__badges-1-icon'
              width={badgeSize}
              height={badgeSize}
              fidelity='low'
              levelNum={1}
            />
          ) : (
            <LevelBadge
              className='proficiency-chart__badges-1-icon'
              width={badgeSize}
              height={badgeSize}
              fidelity='low'
              levelNum={1}
              empty
            />
          )}
          {level >= 2 ? (
            <LevelBadge
              className='proficiency-chart__badges-2-icon'
              width={badgeSize}
              height={badgeSize}
              fidelity='low'
              levelNum={2}
            />
          ) : (
            <LevelBadge
              className='proficiency-chart__badges-2-icon'
              width={badgeSize}
              height={badgeSize}
              fidelity='low'
              levelNum={2}
              empty
            />
          )}
          {level >= 3 ? (
            <LevelBadge
              className='proficiency-chart__badges-3-icon'
              width={badgeSize}
              height={badgeSize}
              fidelity='low'
              levelNum={3}
            />
          ) : (
            <LevelBadge levelNum={3} width={badgeSize} height={badgeSize} empty />
          )}
        </div>
      </WithTooltip>
      {showLabel && label && (
        <div className='proficiency-chart__badges-label'>
          <Text size='xs'>{label}</Text>
        </div>
      )}
    </div>
  );
};

interface ProficiencyMetrics {
  count_student_proficiencies: number;
  // Legacy _end metrics (non-time-series reports)
  count_unpracticed_student_proficiencies_end?: number;
  count_student_proficiencies_level_0_end?: number;
  count_student_proficiencies_level_1_end?: number;
  count_student_proficiencies_level_2_end?: number;
  count_student_proficiencies_level_3_end?: number;
  // New point-in-time metrics (time series reports)
  count_unpracticed_student_proficiencies?: number;
  count_student_proficiencies_level_0?: number;
  count_student_proficiencies_level_1?: number;
  count_student_proficiencies_level_2?: number;
  count_student_proficiencies_level_3?: number;
  // Diagnostic measurement metrics
  count_student_diagnostic_proficiencies_not_started?: number;
  count_student_diagnostic_proficiencies_level_0?: number;
  count_student_diagnostic_proficiencies_level_1?: number;
  count_student_diagnostic_proficiencies_level_2?: number;
  count_student_diagnostic_proficiencies_level_3?: number;
}

interface ProficiencyChartProps {
  showTooltipCounts?: boolean;
  metrics: ProficiencyMetrics;
  iconLabelPosition?: 'bottom' | 'top';
  title?: string;
  onlyBarChart?: boolean;
  barStyle?: CSSProperties;
  showEmpty?: boolean; // if true, shows 'N/A' otherwise renders a bar chart with 100% empty data (ie. not started)
  isStudentDimension?: boolean;
  sortBy?: string | null;
  measurementMethod?: 'practice' | 'diagnostic';
}

/** Slots a method doesn't fill still need a label for the shared percentage readout. */
const labelForSlot = (slot: number) =>
  PROFICIENCY_SEGMENTS_BY_METHOD.practice.segments.find((segment) => segment.index === slot)
    ?.label ?? '';

const IconLabel = ({
  size,
  allNotStarted,
  selectedIcon,
  percentageLabel
}: {
  size: TextProps['size'];
  allNotStarted: boolean;
  selectedIcon: React.ReactNode;
  percentageLabel: string;
}) => (
  <>
    {!allNotStarted && selectedIcon}
    <Text size={size} className='ml-x1'>
      {percentageLabel}
    </Text>
  </>
);

export const ProficiencyChart = ({
  metrics,
  showTooltipCounts,
  iconLabelPosition = 'bottom',
  title = '',
  onlyBarChart = false,
  barStyle,
  showEmpty = true,
  isStudentDimension,
  sortBy,
  measurementMethod = 'practice'
}: ProficiencyChartProps) => {
  const [showPopover, setShowPopover] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  const {segments: segmentDescriptors, totalMetricKey} =
    PROFICIENCY_SEGMENTS_BY_METHOD[measurementMethod];
  // every method fills the same five slots so colors, icons, and popover indexing
  // stay shared; a method that has no bucket for a slot leaves it at zero
  const segmentsToMap = PROFICIENCY_SEGMENT_SLOTS.map((slot) => {
    const descriptor = segmentDescriptors.find((segment) => segment.index === slot);
    return descriptor ? getSegmentCount(metrics, descriptor) : 0;
  });

  const totalCount = totalMetricKey
    ? metrics[totalMetricKey as keyof ProficiencyMetrics]
    : segmentsToMap.reduce((sum, count) => sum + count, 0);

  const colorMap = [
    'var(--color-data-na)',
    'var(--color-data-na-bold)',
    'var(--color-data-proficient)',
    'var(--color-data-excelling)',
    'var(--color-data-mastery)'
  ];

  const normalizedPercents = normalizePercentages(segmentsToMap);

  const segments = segmentsToMap.map((count, index) => {
    const percentage = normalizedPercents[index];
    const label =
      segmentDescriptors.find((descriptor) => descriptor.index === index)?.label ??
      labelForSlot(index);
    const color = colorMap[index];
    return {
      label,
      value: count,
      percentage,
      color
    };
  });

  const isEmpty = totalCount === 0;
  const allNotStarted = segments[0].value === totalCount;

  const selectedIndex = useMemo(() => getSelectedIndex(sortBy), [sortBy]);

  const percentageLabel = useMemo(
    () => getPercentageLabel(segments, selectedIndex, allNotStarted),
    [segments, selectedIndex, allNotStarted]
  );

  const selectedIcon = useMemo(() => getIconForIndex(selectedIndex), [selectedIndex]);

  if (totalCount === 1 && !onlyBarChart) {
    // get the highest proficiency level based on what has been achieved
    // -1 to indicated not started
    const highestProficiencyLevel = segmentsToMap[0]
      ? -1
      : segmentsToMap.findIndex((count) => count > 0) - 1;

    return <ProficiencyChartBadges proficiencyLevel={highestProficiencyLevel} />;
  }

  return (
    <>
      {showPopover && (
        <div
          className={cx({
            'proficiency-chart__pressed': showPopover
          })}
        />
      )}
      <div
        ref={ref}
        className='proficiency-chart'
        role='button'
        tabIndex={0}
        aria-pressed={showPopover}
        onBlur={(e) => {
          if (!ref.current?.contains(e.relatedTarget as Node)) {
            setShowPopover(false);
          }
        }}
        onClick={() => setShowPopover((prev) => !prev)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setShowPopover((prev) => !prev);
          }
        }}
      >
        {title && (
          <div className='proficiency-chart__header'>
            <Text size='xs'>{title}</Text>
            {iconLabelPosition === 'top' && (
              <div className='proficiency-chart__header-icon'>
                <IconLabel
                  size='xs'
                  allNotStarted={allNotStarted}
                  selectedIcon={selectedIcon}
                  percentageLabel={percentageLabel}
                />
              </div>
            )}
          </div>
        )}
        {isEmpty && showEmpty ? (
          <Text size='s'>N/A</Text>
        ) : (
          <SegmentedBar segments={segments} barStyle={barStyle} />
        )}
        {iconLabelPosition === 'bottom' && !isEmpty && (
          <div className='proficiency-chart__footer'>
            <IconLabel
              size='s'
              allNotStarted={allNotStarted}
              selectedIcon={selectedIcon}
              percentageLabel={percentageLabel}
            />
          </div>
        )}
        {showPopover && (
          <Popover
            targetRef={ref}
            position='bottom'
            usePortal={false}
            expanded={showPopover}
            paddingLevel={2}
            roundness='small'
          >
            <div className='proficiency-chart__popover-content gap-x1'>
              {segments.map((segment, index) => {
                if (!segmentDescriptors.some((descriptor) => descriptor.index === index)) {
                  // this method has no bucket for the slot (e.g. diagnostics and Not started)
                  return null;
                }
                const icon = {
                  0: (
                    <div className='proficiency-chart__popover-icon proficiency-chart__popover-icon--not-started' />
                  ),
                  1: (
                    <div className='proficiency-chart__popover-icon proficiency-chart__popover-icon--unranked' />
                  ),
                  2: (
                    <LevelBadge
                      levelNum={1}
                      fidelity='low'
                      width={16}
                      height={16}
                      className='proficiency-chart__badges-1-icon'
                    />
                  ),
                  3: (
                    <LevelBadge
                      levelNum={2}
                      fidelity='low'
                      width={16}
                      height={16}
                      className='proficiency-chart__badges-2-icon'
                    />
                  ),
                  4: (
                    <LevelBadge
                      levelNum={3}
                      fidelity='low'
                      width={16}
                      height={16}
                      className='proficiency-chart__badges-3-icon'
                    />
                  )
                }[index];
                return (
                  <div key={segment.label} className='proficiency-chart__popover-row'>
                    <div>
                      {icon}
                      <Text size='s'>{segment.label}:</Text>
                    </div>
                    <span className='dot-fill' />
                    <div>
                      {showTooltipCounts && (
                        <Text size='s' color='tertiary'>
                          ({segment.value}
                          {isStudentDimension
                            ? ` ${pluralize('skill', segment.value)}`
                            : ` ${pluralize('student', segment.value)}`}
                          )
                        </Text>
                      )}
                      <Text size='s'>{isEmpty ? 0 : segment.percentage}%</Text>
                    </div>
                  </div>
                );
              })}
            </div>
          </Popover>
        )}
      </div>
    </>
  );
};
